"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useState, useSyncExternalStore } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/providers/auth-provider";
import { setGoogleAdsConsent } from "@/lib/actions/google-ads-actions";
import { googleAdsEnabled, GOOGLE_ADS_CONSENT_COOKIE, GOOGLE_ADS_OPEN_SETTINGS_EVENT } from "@/lib/analytics/google-ads-config";
import { denyGoogleAds, hasGoogleAdsConsent } from "@/lib/analytics/google-ads-browser";
import { useGoogleAdsConversion } from "@/hooks/use-google-ads-conversion";
const subscribe = () => () => {};
const hydrated = () => true;
const serverSnapshot = () => false;
export function GoogleAdsMeasurement() {
  const $ = _c(2);
  const ready = useSyncExternalStore(subscribe, hydrated, serverSnapshot);
  let t0;
  if ($[0] !== ready) {
    t0 = googleAdsEnabled() && ready ? <Measurement /> : null;
    $[0] = ready;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
}
function Measurement() {
  const {
    user
  } = useAuth();
  const [open, setOpen] = useState(() => !document.cookie.split('; ').some(c => c.startsWith(`${GOOGLE_ADS_CONSENT_COOKIE}=`)));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [consent, setConsent] = useState(hasGoogleAdsConsent);
  useGoogleAdsConversion(user?.id, user?.email_confirmed_at, consent);
  useEffect(() => {
    const openSettings = () => setOpen(true);
    const sync = () => {
      const granted = hasGoogleAdsConsent();
      if (!granted) denyGoogleAds();
      setConsent(granted);
      if (document.cookie.split('; ').some(c_0 => c_0.startsWith(`${GOOGLE_ADS_CONSENT_COOKIE}=`))) setOpen(false);
    };
    const storage = (event: StorageEvent) => {
      if (event.key === "iloris_ads_consent_changed") sync();
    };
    window.addEventListener("storage", storage);
    window.addEventListener("focus", sync);
    window.addEventListener("pageshow", sync);
    window.addEventListener(GOOGLE_ADS_OPEN_SETTINGS_EVENT, openSettings);
    return () => {
      window.removeEventListener("storage", storage);
      window.removeEventListener("focus", sync);
      window.removeEventListener("pageshow", sync);
      window.removeEventListener(GOOGLE_ADS_OPEN_SETTINGS_EVENT, openSettings);
    };
  }, []);
  async function choose(choice: "granted" | "denied") {
    setBusy(true);
    setError("");
    if (choice === "denied") {
      denyGoogleAds();
      setConsent(false);
      try {
        localStorage.setItem("iloris_ads_consent_changed", String(Date.now()));
      } catch {/* Focus also reconciles peers. */}
    }
    try {
      await setGoogleAdsConsent(choice);
      try {
        localStorage.setItem("iloris_ads_consent_changed", String(Date.now()));
      } catch {/* Cookies remain authoritative. */}
      setConsent(hasGoogleAdsConsent());
      setOpen(false);
    } catch {
      setError("Could not save your preference. Please try again.");
    } finally {
      setBusy(false);
    }
  }
  if (!open) return null;
  return <section aria-label="Google ad measurement" className="fixed bottom-4 left-4 right-4 z-50 max-w-lg rounded-xl border bg-background p-4 text-foreground shadow-lg">
            <h2 className="font-semibold">Help us measure our Google ads?</h2>
            <p className="my-2 text-sm">With your permission, Google uses cookies and receives browser, ad-click, and confirmed-signup information to measure our ads. We do not send your email or use this for personalized ads. This choice only controls Google ad measurement.</p>
            <Link href="/privacy" className="text-sm underline">Privacy policy</Link>
            <p className="mt-2 text-xs">Withdrawing stops our signup events. An already-loaded Google tag may send cookieless consent pings until you leave or reload; your unsaved work stays open.</p>
            <div className="mt-3 flex flex-wrap gap-2">
                <Button variant="outline" disabled={busy} onClick={() => void choose("granted")}>Allow Google ad measurement</Button>
                <Button variant="outline" disabled={busy} onClick={() => void choose("denied")}>No thanks</Button>
                <Button variant="ghost" disabled={busy} onClick={() => setOpen(false)}>Close</Button>
            </div>
            {error && <p role="alert" className="mt-2 text-sm">{error}</p>}
        </section>;
}